import { useCallback, useEffect, useState } from 'react';
import { Clock, Loader2 } from 'lucide-react';
import { Checkbox } from '@/components/ui/checkbox';
import { authenticatedFetch } from '@/lib/api';
import { useRBAC } from '@/contexts/RBACContext';
import { toast } from 'react-toastify';

/**
 * Which clocks count toward a unit's "Updated ..." chip.
 *
 * Each subsystem stamps its own timestamp at its own cadence, and the chip used
 * to read only the Starlink health check's. A unit with no Starlink terminal
 * therefore showed hours stale while its Sol-Ark telemetry was minutes old.
 * Freshness is now the newest of whatever is ticked here.
 *
 * The catalogue comes from the server (api/services/unitFreshness.js) rather
 * than a copy in this file — a source added there shows up here for free.
 */
interface FreshnessSource {
  key: string;
  label: string;
  description: string;
  /** Resolved server-side against linked device rows, not hardcoded. */
  available: boolean;
}

export function UnitFreshnessCard() {
  const { isSuperadmin } = useRBAC();
  const [sources, setSources] = useState<string[]>([]);
  const [available, setAvailable] = useState<FreshnessSource[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  const fetchSetting = useCallback(async () => {
    // The route is superadmin-only and the card renders nothing otherwise —
    // no reason to spend a request that can only come back 403.
    if (!isSuperadmin) {
      setIsLoading(false);
      return;
    }
    try {
      const response = await authenticatedFetch('/api/v1/admin/settings/unit-freshness');
      // A non-OK response used to fall through silently and render an empty
      // list, which reads as "there are no sources" rather than "this failed".
      if (!response.ok) throw new Error(`Request failed (${response.status})`);
      const data = await response.json();
      setSources(Array.isArray(data.sources) ? data.sources : []);
      setAvailable(Array.isArray(data.available) ? data.available : []);
      setLoadError(null);
    } catch (error) {
      console.error('Failed to fetch unit freshness setting:', error);
      setLoadError(error instanceof Error ? error.message : 'Could not load freshness sources');
    } finally {
      setIsLoading(false);
    }
  }, [isSuperadmin]);

  useEffect(() => {
    fetchSetting();
  }, [fetchSetting]);

  // Fire-on-change, matching the Sol-Ark interval selectors — no Save button.
  const handleToggle = async (key: string, checked: boolean) => {
    const next = checked
      ? available.filter(s => sources.includes(s.key) || s.key === key).map(s => s.key)
      : sources.filter(k => k !== key);

    // An empty selection has no defined freshness; the server rejects it too.
    if (next.length === 0) return;

    const previous = sources;
    setSources(next);
    setIsSaving(true);
    try {
      const response = await authenticatedFetch('/api/v1/admin/settings/unit-freshness', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sources: next }),
      });

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.message || 'Save failed');
      }
      const data = await response.json();
      setSources(data.sources);
      toast.success('Freshness sources saved');
    } catch (error) {
      setSources(previous);
      toast.error(error instanceof Error ? error.message : 'Save failed');
    } finally {
      setIsSaving(false);
    }
  };

  if (!isSuperadmin) return null;

  return (
    <div className="bg-card border border-border rounded-xl p-6">
      <h2 className="text-lg font-semibold text-white mb-6 flex items-center gap-2">
        <Clock className="w-5 h-5 text-[#8FB560]" />
        Data Freshness
        {isSaving && <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />}
      </h2>

      <p className="text-xs text-muted-foreground mb-4">
        A unit&apos;s &quot;Updated&quot; time is the most recent of the sources selected here.
        Leaving a source off hides its clock even when it is the freshest.
      </p>

      {isLoading ? (
        <div className="text-xs text-muted-foreground">Loading…</div>
      ) : loadError ? (
        <div role="alert" className="text-xs text-destructive">
          Could not load freshness sources: {loadError}
        </div>
      ) : (
        <div className="space-y-2">
          {available.map(source => {
            const isEnabled = sources.includes(source.key);
            const isLastEnabled = isEnabled && sources.length === 1;
            // Unavailable blocks turning a source ON, never turning it OFF —
            // otherwise unlinking the last device strands an enabled source as
            // checked-and-disabled with no way to remove it.
            const isUnavailable = !source.available && !isEnabled;

            return (
              <div
                key={source.key}
                className="flex items-start gap-3 p-3 bg-background rounded-lg border border-border"
              >
                <Checkbox
                  id={`freshness-${source.key}`}
                  checked={isEnabled}
                  onCheckedChange={checked => handleToggle(source.key, checked === true)}
                  disabled={isSaving || isLastEnabled || isUnavailable}
                  className="mt-0.5 data-[state=checked]:bg-[#8FB560] data-[state=checked]:border-[#8FB560]"
                />
                <div className="min-w-0">
                  <label
                    htmlFor={`freshness-${source.key}`}
                    className="text-sm font-medium text-white cursor-pointer"
                  >
                    {source.label}
                  </label>
                  <p className="text-xs text-muted-foreground">
                    {source.description}
                    {isUnavailable && ' No devices linked yet.'}
                  </p>
                </div>
              </div>
            );
          })}
          <p className="text-[10px] text-muted-foreground pt-1">
            At least one source must stay selected.
          </p>
        </div>
      )}
    </div>
  );
}
